/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Input } from '@/components/ui/input';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { getTurnstileSiteKey, getTurnstileMode, isTurnstileEnabled } from '@/lib/turnstile/utils';

export interface TrackOrderLookupFormProps {
    /** A resolved, user-facing error message from the previous submit, if any. */
    error?: string;
    /**
     * Site/locale-prefixed target for the POST. Defaults to the current route (the
     * `/track-order` page itself). Pass this when embedding the form on another route
     * (e.g. `/login`) so the submit still reaches the `/track-order` route's `action`.
     */
    action?: string;
}

/**
 * The guest Track Order lookup form: order number + email + billing postal code, POSTed to
 * the `/track-order` route action. A navigating `<Form>` (not `useFetcher`) so a successful
 * lookup swaps this form for the order view on the same URL — the SFNext analogue of SFRA's
 * Order-Track → Order-GuestDetails redirect.
 *
 * Bot protection: when Turnstile is enabled the widget renders here and its token rides along in
 * a hidden `turnstileToken` input; the route action verifies it at the BFF before the lookup
 * runs. When Turnstile is disabled nothing renders and the action's check is a no-op.
 */
export function TrackOrderLookupForm({ error, action }: TrackOrderLookupFormProps) {
    const { t } = useTranslation('trackOrder');
    const config = useConfig();

    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const turnstileEnabled = config ? isTurnstileEnabled(config) : false;
    const turnstileMode = config ? getTurnstileMode(config) : 'managed';
    const turnstileSiteKey = useMemo(() => {
        if (!config || !turnstileEnabled) return null;
        // Site key lookup is hostname-based, so it needs the client origin.
        if (typeof window !== 'undefined') {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            return getTurnstileSiteKey(config, baseUrl);
        }
        return null;
    }, [config, turnstileEnabled]);

    const handleTurnstileSuccess = useCallback((token: string) => {
        setTurnstileToken(token);
    }, []);

    const handleTurnstileReset = useCallback(() => {
        setTurnstileToken(null);
    }, []);

    return (
        <Form method="post" action={action} className="space-y-6" noValidate>
            {error && (
                <div
                    className="p-3 bg-destructive/10 rounded-ui border border-destructive/20"
                    role="alert"
                    data-testid="track-order-error">
                    <p className="text-sm text-destructive">{error}</p>
                </div>
            )}

            <div>
                <label htmlFor="track-order-number" className="block text-sm font-medium text-foreground">
                    {t('form.orderNumberLabel')}
                </label>
                <Input
                    id="track-order-number"
                    name="orderNo"
                    type="text"
                    autoComplete="off"
                    required
                    className="mt-1"
                    placeholder={t('form.orderNumberPlaceholder')}
                />
            </div>

            <div>
                <label htmlFor="track-order-email" className="block text-sm font-medium text-foreground">
                    {t('form.emailLabel')}
                </label>
                <Input
                    id="track-order-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="mt-1"
                    placeholder={t('form.emailPlaceholder')}
                />
            </div>

            <div>
                <label htmlFor="track-order-postal" className="block text-sm font-medium text-foreground">
                    {t('form.postalCodeLabel')}
                </label>
                <Input
                    id="track-order-postal"
                    name="postalCode"
                    type="text"
                    autoComplete="postal-code"
                    required
                    className="mt-1"
                    placeholder={t('form.postalCodePlaceholder')}
                />
            </div>

            {turnstileEnabled && turnstileSiteKey && (
                <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileReset}
                    onExpire={handleTurnstileReset}
                    enabled={turnstileEnabled}
                    mode={turnstileMode}
                />
            )}

            {turnstileToken && <input type="hidden" name="turnstileToken" value={turnstileToken} />}

            <FormSubmitButton defaultText={t('form.submit')} submittingText={t('form.submitting')} />
        </Form>
    );
}
